/**
 * "What's missing before this container can really run" — ONE rule used by
 * the board's hint icons and the editor tabs' warning dots, so the two
 * surfaces can never disagree. Each hint names the gap, its consequence,
 * and the tab that fixes it.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { EditorTabKey } from '../components/editor/EditorTabs';
import type { DropGenContainerSummary } from '../drop-generator.types';

export type SetupHintKey =
  | 'groups'
  | 'slots'
  | 'slotNoGroup'
  | 'gateways'
  | 'texts'
  | 'senders';

export interface SetupHint {
  key: SetupHintKey;
  tab: EditorTabKey;
  label: string;
}

const HINTS: Record<SetupHintKey, SetupHint> = {
  groups: {
    key: 'groups',
    tab: 'groups',
    label: 'No list groups linked — nothing to send to. Link them on the List groups tab.',
  },
  slots: {
    key: 'slots',
    tab: 'general',
    label: 'No drop slots — nothing gets planned. Add them under General & Slots → Drop slots.',
  },
  slotNoGroup: {
    key: 'slotNoGroup',
    tab: 'general',
    label:
      'Some drops have no group — they are not planned. Give each one a group under General & Slots → Drop slots.',
  },
  gateways: {
    key: 'gateways',
    tab: 'gateways',
    label: 'No gateways in the pool — drops get no route. Add them on the Gateways tab.',
  },
  texts: {
    key: 'texts',
    tab: 'creative',
    label: 'No prompt groups — generated drafts get no texts. Pick them on the Creative tab.',
  },
  senders: {
    key: 'senders',
    tab: 'gateways',
    label: "No sender group — generated drafts get no senders. Set it in the Gateways tab's container defaults.",
  },
};

export interface SetupFacts {
  groupCount: number;
  /** Phase 21 — enabled drops naming no linked group (never planned). */
  dropsWithoutGroup?: number;
  slotCount: number;
  poolCount: number;
  textGroupCount: number;
  hasSenderGroup: boolean;
}

export function containerSetupHints(facts: SetupFacts): SetupHint[] {
  const out: SetupHint[] = [];
  if (facts.groupCount === 0) out.push(HINTS.groups);
  if (facts.slotCount === 0) out.push(HINTS.slots);
  if ((facts.dropsWithoutGroup ?? 0) > 0) out.push(HINTS.slotNoGroup);
  if (facts.poolCount === 0) out.push(HINTS.gateways);
  if (facts.textGroupCount === 0) out.push(HINTS.texts);
  if (!facts.hasSenderGroup) out.push(HINTS.senders);
  return out;
}

/** The board row works off the plan summary (its groups + its drops). */
export function summarySetupFacts(c: DropGenContainerSummary): SetupFacts {
  return {
    groupCount: c.groupCount,
    slotCount: c.slotCount,
    poolCount: c.poolCount,
    textGroupCount: c.textGroupCount,
    hasSenderGroup: c.hasSenderGroup,
    dropsWithoutGroup: dropsWithoutGroup(c.groups ?? [], c.slots),
  };
}

/** The editor works off the LIVE draft, so dots clear as gaps are fixed. */
export function draftSetupFacts(draft: {
  groups: Array<{ listGroupId: string }>;
  slots: Array<{ enabled?: boolean; listGroupIds?: string[] }>;
  gateway: { pool: unknown[]; senderDefaults: { senderGroupId: string | null } };
  creative: { textGroupIds: string[] };
}): SetupFacts {
  return {
    groupCount: draft.groups.length,
    slotCount: draft.slots.length,
    poolCount: draft.gateway.pool.length,
    textGroupCount: draft.creative.textGroupIds.length,
    hasSenderGroup: Boolean(draft.gateway.senderDefaults.senderGroupId),
    dropsWithoutGroup: dropsWithoutGroup(draft.groups, draft.slots),
  };
}

/** Enabled drops that name no linked group — the engine never plans them. */
function dropsWithoutGroup(
  groups: Array<{ listGroupId: string }>,
  slots: Array<{ enabled?: boolean; listGroupIds?: string[] }>,
): number {
  if (groups.length === 0) return 0; // the "no list groups" hint says it
  const linked = new Set(groups.map((g) => g.listGroupId));
  return slots.filter((s) => s.enabled !== false && !(s.listGroupIds ?? []).some((g) => linked.has(g))).length;
}

/** A master config has no lists and no slots — those gaps are each container's. */
const CONTAINER_ONLY_HINTS: ReadonlySet<SetupHintKey> = new Set(['groups', 'slots', 'slotNoGroup']);

export function hintsFor(kind: 'container' | 'master', hints: SetupHint[]): SetupHint[] {
  return kind === 'master' ? hints.filter((h) => !CONTAINER_ONLY_HINTS.has(h.key)) : hints;
}
